/* eslint-disable @typescript-eslint/no-unsafe-argument */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */

type GetIndexedField<T, K> = K extends keyof T
    ? T[K]
    : K extends `${number}`
      ? '0' extends keyof T
          ? undefined
          : number extends keyof T
            ? T[number]
            : undefined
      : undefined;

type FieldWithPossiblyUndefined<T, Key> =
    | GetFieldType<Exclude<T, undefined>, Key>
    | Extract<T, undefined>;

type IndexedFieldWithPossiblyUndefined<T, Key> =
    | GetIndexedField<Exclude<T, undefined>, Key>
    | Extract<T, undefined>;

export type GetFieldType<T, P> = P extends `${infer Left}.${infer Right}`
    ? Left extends keyof T
        ? FieldWithPossiblyUndefined<T[Left], Right>
        : Left extends `${infer FieldKey}[${infer IndexKey}]`
          ? FieldKey extends keyof T
              ? FieldWithPossiblyUndefined<
                    IndexedFieldWithPossiblyUndefined<T[FieldKey], IndexKey>,
                    Right
                >
              : undefined
          : undefined
    : P extends keyof T
      ? T[P]
      : P extends `${infer FieldKey}[${infer IndexKey}]`
        ? FieldKey extends keyof T
            ? IndexedFieldWithPossiblyUndefined<T[FieldKey], IndexKey>
            : undefined
        : undefined;

export function getValue<
    TData,
    TPath extends string | string[] | undefined | null,
    TDefault = GetFieldType<TData, TPath>,
>(
    data: TData,
    path?: TPath,
    defaultValue?: TDefault,
): GetFieldType<TData, TPath> | TDefault | undefined {
    if (!path) {
        return undefined;
    }

    const pathArray = (Array.isArray(path) ? path : path.split(/[.[\]]/)).filter(Boolean);

    const result = pathArray.reduce<GetFieldType<TData, TPath>>(
        (prevObj, key) => prevObj && (prevObj as any)?.[key],
        data as any,
    );

    return result === undefined ? (defaultValue as TDefault) : result;
}

export function setValue<
    TData,
    TPath extends string | string[],
    TDefault = GetFieldType<TData, TPath>,
>(data: TData, path: TPath, defaultValue?: TDefault) {
    const pathArray = (Array.isArray(path) ? path : path.split(/[.[\]]/)).filter(Boolean);

    pathArray.reduce<GetFieldType<TData, TPath>>((value, key, i) => {
        if ((value as any)?.[key] === undefined) (value as any)[key as typeof value] = {};
        if (i === pathArray.length - 1) (value as any)[key as typeof value] = defaultValue;
        return (value as any)?.[key];
    }, data as any);
}

export function omitBy<T extends Record<string, any>>(
    obj: T | undefined,
    check: (value: T[keyof T]) => boolean,
): Partial<T> {
    if (!obj) return {};
    obj = { ...obj };
    Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && check(value)) {
            obj && delete obj[key as keyof T];
        }
    });
    return obj;
}
